// EJE FRONTEND (ARQUITECTURA_FRONT_PROPUESTA.md §7): hooks TanStack Query por caso de uso del
// dominio `retail`. Componen el service-client `api/services/retail-org/` (dueño = Andrés). Query
// keys namespaced por dominio (['retail', ...]) para no chocar con otras invalidaciones.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    listRetailers,
    createRetailer,
    updateRetailer,
    changeRetailerStatus,
    createSubdistributor,
    listZones,
    createZone,
    updateZone,
    changeZoneStatus,
    listStores,
    createStore,
    updateStore,
    changeStoreStatus,
    listRetailUsers,
    createRetailUser,
    provisionRetailUser,
    updateRetailUser,
    changeRetailUserStatus,
    deleteRetailUser,
    transferRetailUser,
    getRetailerHierarchy,
    lookupRetailUserWallet,
    linkRetailUserWallet,
    unlinkRetailUserWallet,
    type Retailer,
    type Zone,
    type Store,
    type RetailUser,
    type RetailerHierarchy,
    type CreateRetailerInput,
    type CreateStoreInput,
} from '@/api/services/retail-org/retail-org.client';
import type { CreateRetailerRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/CreateRetailerRequest';
import type { CreateZoneRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/CreateZoneRequest';
import type { CreateStoreRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/CreateStoreRequest';
import type { CreateRetailUserRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/CreateRetailUserRequest';
import type { ProvisionRetailUserRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/ProvisionRetailUserRequest';
import type { UpdateZoneRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/UpdateZoneRequest';
import type { UpdateStoreRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/UpdateStoreRequest';
import type { UpdateRetailUserRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/UpdateRetailUserRequest';
import type { RetailOrg } from '@fiado/type-kit';
import { commissionKeys } from '@/domains/retail/admin/comisiones/api/commissionQueries';

const KEYS = {
    retailers: ['retail', 'retailers'] as const,
    hierarchy: ['retail', 'hierarchy'] as const,
    zones: (retailerId: string) => ['retail', 'zones', retailerId] as const,
    stores: (retailerId: string) => ['retail', 'stores', retailerId] as const,
    users: (retailerId: string) => ['retail', 'users', retailerId] as const,
};

// ── Retailers (B01 — lista) ──────────────────────────────────────────────

/** GET /retailers — lista de negocios (B01). */
export function useRetailers() {
    return useQuery({ queryKey: KEYS.retailers, queryFn: () => listRetailers() });
}

/** POST /retailers — alta de negocio raíz (Test 2). */
export function useCreateRetailer() {
    const qc = useQueryClient();
    return useMutation({
        // TD-SKF1: el client tipa el body como `CreateRetailerInput` (Omit del entity). El wire real
        // es `CreateRetailerRequest`. Se castea hasta alinear los input-types del client a los DTOs.
        mutationFn: (body: CreateRetailerRequest) =>
            createRetailer(body as unknown as CreateRetailerInput, crypto.randomUUID()),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.retailers }),
    });
}

/** PUT /retailers/{id} — editar negocio. */
export function useUpdateRetailer() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            retailerId,
            body,
        }: {
            retailerId: string;
            body: Partial<CreateRetailerRequest>;
        }) => updateRetailer(retailerId, body as unknown as Partial<CreateRetailerInput>),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.retailers }),
    });
}

/** PUT /retailers/{id}/status — activar / dar de baja un negocio. */
export function useChangeRetailerStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ retailerId, status }: { retailerId: string; status: string }) =>
            changeRetailerStatus(retailerId, status),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.retailers }),
    });
}

/** POST /retailers/{parentId}/subdistributors — alta de subdistribuidor/franquicia. */
export function useCreateSubdistributor() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ parentId, body }: { parentId: string; body: CreateRetailerRequest }) =>
            createSubdistributor(
                parentId,
                body as unknown as CreateRetailerInput,
                crypto.randomUUID(),
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: KEYS.retailers });
            void qc.invalidateQueries({ queryKey: KEYS.hierarchy });
        },
    });
}

// ── Zonas (Test 3) ───────────────────────────────────────────────────────

/** GET /retailers/{id}/zones. */
export function useZones(retailerId: string) {
    return useQuery({
        queryKey: KEYS.zones(retailerId),
        queryFn: () => listZones(retailerId),
        enabled: !!retailerId,
    });
}

/** POST /retailers/{id}/zones. */
export function useCreateZone(retailerId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: CreateZoneRequest) => createZone(retailerId, body, crypto.randomUUID()),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.zones(retailerId) }),
    });
}

/** PUT /retailers/{id}/zones/{zoneId}/status. */
export function useChangeZoneStatus(retailerId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ zoneId, status }: { zoneId: string; status: string }) =>
            changeZoneStatus(retailerId, zoneId, status),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.zones(retailerId) }),
    });
}

// ── Tiendas (Test 3) ─────────────────────────────────────────────────────

/** GET /retailers/{id}/stores. */
export function useStores(retailerId: string) {
    return useQuery({
        queryKey: KEYS.stores(retailerId),
        queryFn: () => listStores(retailerId),
        enabled: !!retailerId,
    });
}

/** POST /retailers/{id}/stores — `location` (lat/lng) es OBLIGATORIA. */
export function useCreateStore(retailerId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: CreateStoreRequest) =>
            createStore(retailerId, body as unknown as CreateStoreInput, crypto.randomUUID()),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.stores(retailerId) }),
    });
}

/** PUT /retailers/{id}/stores/{storeId}/status. */
export function useChangeStoreStatus(retailerId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ storeId, status }: { storeId: string; status: string }) =>
            changeStoreStatus(retailerId, storeId, status),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.stores(retailerId) }),
    });
}

// ── Usuarios retail (Test 4) ─────────────────────────────────────────────

/** GET /retailers/{id}/users. */
export function useRetailUsers(retailerId: string) {
    return useQuery({
        queryKey: KEYS.users(retailerId),
        queryFn: () => listRetailUsers(retailerId),
        enabled: !!retailerId,
    });
}

/** POST /retailers/{id}/users — valida el `cognitoSub` contra platform-rbac (412 si no tiene rol). */
export function useCreateRetailUser(retailerId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: CreateRetailUserRequest) =>
            createRetailUser(retailerId, body, crypto.randomUUID()),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.users(retailerId) }),
    });
}

/**
 * POST /retailers/{id}/users/provision — alta ORQUESTADA: RBAC provisiona al usuario (Cognito + user +
 * grant, queda INVITED) y retail crea el RetailUser con el cognitoSub retornado. Un solo click.
 */
export function useProvisionRetailUser(retailerId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: ProvisionRetailUserRequest) =>
            provisionRetailUser(retailerId, body, crypto.randomUUID()),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.users(retailerId) }),
    });
}

/** PUT /retailers/{id}/users/{userId}/status. */
export function useChangeRetailUserStatus(retailerId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ retailUserId, status }: { retailUserId: string; status: string }) =>
            changeRetailUserStatus(retailerId, retailUserId, status),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.users(retailerId) }),
    });
}

/** DELETE /retailers/{id}/users/{userId}. */
export function useDeleteRetailUser(retailerId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (retailUserId: string) => deleteRetailUser(retailerId, retailUserId),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.users(retailerId) }),
    });
}

/** PUT /retailers/{id}/users/{userId}/transfer — traslada el usuario a otra tienda. */
export function useTransferRetailUser(retailerId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            retailUserId,
            homeStoreId,
        }: {
            retailUserId: string;
            homeStoreId: string;
        }) => transferRetailUser(retailerId, retailUserId, homeStoreId),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.users(retailerId) }),
    });
}

// ── Updates + jerarquía (endpoints extra) ────────────────────────────────

/** PUT /retailers/{id}/zones/{zoneId}. */
export function useUpdateZone(retailerId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ zoneId, body }: { zoneId: string; body: UpdateZoneRequest }) =>
            updateZone(retailerId, zoneId, body),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.zones(retailerId) }),
    });
}

/** PUT /retailers/{id}/stores/{storeId}. */
export function useUpdateStore(retailerId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ storeId, body }: { storeId: string; body: UpdateStoreRequest }) =>
            updateStore(retailerId, storeId, body as unknown as Partial<CreateStoreInput>),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.stores(retailerId) }),
    });
}

/** PUT /retailers/{id}/users/{userId}. */
export function useUpdateRetailUser(retailerId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            retailUserId,
            body,
        }: {
            retailUserId: string;
            body: UpdateRetailUserRequest;
        }) => updateRetailUser(retailerId, retailUserId, body),
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.users(retailerId) }),
    });
}

/** GET /retailers/{id}/hierarchy — árbol Retailer → Zona → Tienda → Usuario. */
export function useRetailerHierarchy(retailerId: string, enabled: boolean) {
    return useQuery({
        queryKey: ['retail', 'hierarchy', retailerId] as const,
        queryFn: () => getRetailerHierarchy(retailerId),
        enabled: !!retailerId && enabled,
    });
}

// ── Wallet PCF del usuario ───────────────────────────────────────────────

/** Lo que la pantalla hace al terminar: corre aunque el modal ya se haya cerrado. */
interface WalletWriteHandlers {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
}

/** Con wallet nueva o sin ella cambia el motivo de sus pagos pendientes en Por pagar. */
function invalidateAfterWallet(qc: ReturnType<typeof useQueryClient>, retailerId: string) {
    return Promise.all([
        qc.invalidateQueries({ queryKey: KEYS.users(retailerId) }),
        qc.invalidateQueries({ queryKey: commissionKeys.payments() }),
    ]);
}

/** POST /retailers/{id}/users/wallet-lookup — solo lee: si existe, si está activa y de quién es. */
export function useLookupRetailUserWallet(retailerId: string) {
    return useMutation({
        mutationFn: (body: RetailOrg.LookupRetailUserWalletRequest) =>
            lookupRetailUserWallet(retailerId, body),
    });
}

/** PUT /retailers/{id}/users/{userId}/wallet — el servidor vuelve a verificar la cuenta. */
export function useLinkRetailUserWallet(retailerId: string, handlers: WalletWriteHandlers = {}) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            retailUserId,
            body,
        }: {
            retailUserId: string;
            body: RetailOrg.LinkRetailUserWalletRequest;
        }) => linkRetailUserWallet(retailerId, retailUserId, body),
        onSuccess: () => {
            handlers.onSuccess?.();
            return invalidateAfterWallet(qc, retailerId);
        },
        onError: handlers.onError,
    });
}

/** DELETE /retailers/{id}/users/{userId}/wallet. */
export function useUnlinkRetailUserWallet(retailerId: string, handlers: WalletWriteHandlers = {}) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (retailUserId: string) => unlinkRetailUserWallet(retailerId, retailUserId),
        onSuccess: () => {
            handlers.onSuccess?.();
            return invalidateAfterWallet(qc, retailerId);
        },
        onError: handlers.onError,
    });
}

export type { Retailer, Zone, Store, RetailUser, RetailerHierarchy };
